/**
 * M9.0：列类型注册表 + 「规范值域」转换模型（前端唯一真源）。
 *
 * 设计见 docs/M9-COLUMN-TYPES.md §9.3 / §9.5：
 * 任意类型只与 5 个规范值域打交道，转换 = fromCanonical_B(toCanonical_A(v))，
 * 规则数从 O(n²) 降到 O(n)。后端 StructureEditService.cpp 里有同一份表（kColumnTypes），
 * **两边必须保持一致**（改一处要同步另一处）。
 *
 * `implemented` 表示「编辑器是否已实现」：M9 分批推进时逐个翻 true。
 * 未实现的类型后端也接受（导入用），但前端不给编辑入口，只读展示。
 */

/** 单元格实现形态（决定用哪个组件族） */
export type ColumnClass = 'inline' | 'toggle' | 'popup' | 'derived' | 'settings'

/** 规范值域 */
export type Canonical = 'string' | 'number' | 'time' | 'range' | 'refs'

export interface ColumnTypeSpec {
  /** monday 的 type id（与 DB 的 BoardColumn.Type 一致） */
  id: string
  /** 界面中文名 */
  label: string
  /** 实现形态 */
  cls: ColumnClass
  /** 规范值域 */
  canonical: Canonical
  /** 编辑器是否已实现（M9 分批翻牌） */
  implemented: boolean
  /** 是否多值（数组语义，便于 UI 判断） */
  multiValue?: boolean
}

/** 24 种数据列类型（不含 name 系统列），顺序与后端 kColumnTypes 一致 */
export const COLUMN_TYPES: ColumnTypeSpec[] = [
  { id: 'text', label: '文本', cls: 'inline', canonical: 'string', implemented: true },
  { id: 'long-text', label: '长文本', cls: 'inline', canonical: 'string', implemented: true },
  { id: 'numeric', label: '数字', cls: 'inline', canonical: 'number', implemented: true },
  { id: 'date', label: '日期', cls: 'popup', canonical: 'time', implemented: true },
  { id: 'color', label: '状态', cls: 'popup', canonical: 'refs', implemented: true },
  { id: 'dropdown', label: '下拉', cls: 'popup', canonical: 'refs', implemented: true, multiValue: true },
  { id: 'phone', label: '电话', cls: 'inline', canonical: 'string', implemented: true },
  { id: 'country', label: '国家', cls: 'inline', canonical: 'string', implemented: true },
  { id: 'hour', label: '小时', cls: 'inline', canonical: 'time', implemented: true },
  { id: 'email', label: '邮箱', cls: 'popup', canonical: 'string', implemented: true },
  { id: 'link', label: '链接', cls: 'popup', canonical: 'string', implemented: true },
  { id: 'timerange', label: '时间线', cls: 'popup', canonical: 'range', implemented: true },
  { id: 'tag', label: '标签', cls: 'popup', canonical: 'refs', implemented: true, multiValue: true },
  { id: 'week', label: '周', cls: 'popup', canonical: 'time', implemented: true },
  { id: 'boolean', label: '复选框', cls: 'toggle', canonical: 'number', implemented: true },
  { id: 'rating', label: '评分', cls: 'toggle', canonical: 'number', implemented: true },
  { id: 'color-picker', label: '颜色', cls: 'popup', canonical: 'string', implemented: true },
  { id: 'file', label: '文件', cls: 'popup', canonical: 'refs', implemented: true, multiValue: true },
  { id: 'duration', label: '计时', cls: 'popup', canonical: 'range', implemented: true, multiValue: true },
  { id: 'formula', label: '公式', cls: 'settings', canonical: 'number', implemented: true },
  { id: 'pulse-id', label: '条目 ID', cls: 'derived', canonical: 'number', implemented: true },
  { id: 'pulse-log', label: '创建时间', cls: 'derived', canonical: 'time', implemented: true },
  { id: 'pulse-updated', label: '更新时间', cls: 'derived', canonical: 'time', implemented: true },
  { id: 'autonumber', label: '自动编号', cls: 'derived', canonical: 'number', implemented: true },
  // monday 的 Progress 列（内部名 Battery）：只读派生，按 related_columns 权重计算百分比
  { id: 'columns-battery', label: '进度', cls: 'derived', canonical: 'number', implemented: true },
]

const BY_ID = new Map(COLUMN_TYPES.map((t) => [t.id, t]))

/** 系统列 name */
export const SYSTEM_NAME_TYPE = 'name'

export function typeSpec(id: string): ColumnTypeSpec | undefined {
  return BY_ID.get(id)
}

/** 是否注册表内的类型（含 name 系统列） */
export function isKnownType(id: string): boolean {
  return id === SYSTEM_NAME_TYPE || BY_ID.has(id)
}

/** 编辑器是否已实现（只有已实现的才允许编辑/新建） */
export function isImplementedType(id: string): boolean {
  if (id === SYSTEM_NAME_TYPE) return true
  return BY_ID.get(id)?.implemented === true
}

/** 已实现类型（新建列/改类型菜单用） */
export const IMPLEMENTED_COLUMN_TYPES: ColumnTypeSpec[] = COLUMN_TYPES.filter((t) => t.implemented)

/** 类型中文名（未知类型原样返回，便于排错） */
export function columnTypeLabel(id: string): string {
  if (id === SYSTEM_NAME_TYPE) return '名称'
  return BY_ID.get(id)?.label ?? id
}

/** 规范值域的可转换判定 —— 与后端 CanConvertDomain 完全一致 */
export function canConvert(fromType: string, toType: string): boolean {
  const from = typeSpec(fromType)?.canonical
  const to = typeSpec(toType)?.canonical
  if (from === undefined || to === undefined) return false
  if (to === 'string') return true
  if (from === to) return true
  if (to === 'number' && (from === 'time' || from === 'refs')) return true
  if (to === 'time' && (from === 'number' || from === 'refs')) return true
  if (to === 'refs' && (from === 'string' || from === 'number' || from === 'time')) return true
  return false
}
